import { useState, useMemo, useCallback, useContext } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { Button } from "@aidkitorg/component-library";
import { usePost } from "../../API";
import { useModularMarkdown } from "../../Hooks/ModularMarkdown";
import InterfaceContext from "../../Context";
import { useLocalizedStrings } from "../../Localization";
import { DistroQuestionProps } from "../Props";
import { ConnectionModal } from "./ConnectionModal";

export function DeckVerification({
  component,
  info,
  setInfo,
}: DistroQuestionProps<v0.DeckVerification>) {
  const L = useLocalizedStrings();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const createLinkToken = usePost("/deck/create_link_token");
  const context = useContext(InterfaceContext);
  const currentState = info[component.targetField] || "";

  const defaultText = L.questions.deck_verification;
  const header = useModularMarkdown({ content: component.header || defaultText.header, info });
  const questionDescription = useModularMarkdown({ content: component.questionDescription || "", info });
  const connectedDescription = useModularMarkdown({ content: component.connectedDescription || "", info });
  const optedOutDescription = useModularMarkdown({ content: component.optedOutDescription || "", info });
  const verifyButtonText = useModularMarkdown({ content: component.verifyButtonText || defaultText.verify_button_text, info, noParagraphs: true });
  const reverifyButtonText = useModularMarkdown({ content: component.reverifyButtonText || defaultText.reverify_button_text, info, noParagraphs: true });
  const optOutButtonText = useModularMarkdown({ content: component.optOutButtonText || defaultText.opt_out_button_text, info, noParagraphs: true });

  const completedDescription = useMemo(() => {
    switch (currentState) {
      case "pending":
      case "processed":
        return connectedDescription;
      case "opted_out":
        return optedOutDescription;
      default:
        return null;
    }
  }, [currentState, connectedDescription, optedOutDescription]);

  const primaryButtonText = ["pending", "processed"].includes(currentState)
    ? reverifyButtonText
    : verifyButtonText;

  const handleClickVerify = useCallback(async () => {
    const response = await createLinkToken({ lang: context.lang, type: component.verificationType.type });

    if (response) {
      setLinkToken(response.link_token);
    }
  }, [createLinkToken]);

  const handleClickOptOut = useCallback(() => {
    setInfo(component.targetField, "opted_out");
  }, [setInfo]);

  const handleSubmit = useCallback((connectionId: string) => {
    setLinkToken(null);
    setInfo(component.targetField, "pending");
    setInfo(component.targetField + "_connection_id", connectionId);
  }, [setInfo]);

  const handleExit = useCallback(() => {
    setLinkToken(null);
  }, []);

  return (
    <fieldset>
      <legend>
        {header}
        {questionDescription}
      </legend>
      {linkToken &&
        <ConnectionModal
          component={component}
          token={linkToken}
          onSubmit={handleSubmit}
          onExit={handleExit}
        />
      }
      <div className="mt-2">
        <Button
          variant="primary"
          onClick={handleClickVerify}
        >
          {primaryButtonText}
        </Button>
      </div>

      {!currentState && // Applicants can opt-out if they haven't answered this question before
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleClickOptOut}
          >
            {optOutButtonText}
          </Button>
        </div>
      }

      {completedDescription && 
        <div className="mt-3">
          {completedDescription}
        </div>
      }
    </fieldset>
  );
};
